import React, { FC, useId } from 'react';
import { Control, useController } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../components/combobox/index';
import Loader from '../../../../components/loader/index';
import { useAllSharePlans } from '../../../../hooks/share-plan/use-all-share-plans';
import { FormSchema } from '../event-form.validation';

const SHARE_PLAN_FIELD = 'stepOne.eventDetails.grant.sharePlan' as const;

const buildSharePlanFormValue = (plan: { id: string; name: string; pool?: { id: string } }) => {
  return {
    id: plan.id,
    name: plan.name,
    pool: { id: plan.pool?.id ?? '' },
  };
};

export type EquityPlanComboboxProps = {
  companyId: string;
  control: Control<FormSchema>;
};

const ADD_PLAN_BUTTON_LABEL = (
  <>
    + <AppFormattedMessage id="ADD_NEW_PLAN" />
  </>
);

export const EquityPlanCombobox: FC<EquityPlanComboboxProps> = ({ companyId, control }) => {
  const navigate = useNavigate();
  const scrollableTargetId = useId();
  const { field } = useController({ control, name: SHARE_PLAN_FIELD });
  const { sharePlans, fetchNextPage, hasNextPage, isPending } = useAllSharePlans({ companyId });
  const displayValue = field.value?.name ?? '';

  return (
    <FormCombobox className="relative" control={control} name={SHARE_PLAN_FIELD}>
      <div className="relative">
        <FormComboboxInput
          control={control}
          customValue={() => displayValue}
          icon={<ChevronDownIcon className="mt-3 h-4 w-4 text-gray-500" />}
          name={SHARE_PLAN_FIELD}
          placeholder={displayValue ? undefined : <AppFormattedMessage id="PLEASE_SELECT" />}
          readOnly
          wrapperClassName="max-h-[40px]"
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>
      <ComboboxOptions
        className="absolute z-10 max-h-[280px] w-full overflow-y-auto rounded border border-gray-200 bg-white p-2 shadow-lg"
        id={scrollableTargetId}
      >
        <div className="flex w-full flex-col gap-2">
          {isPending ? (
            <div className="flex justify-center py-4">
              <Loader />
            </div>
          ) : sharePlans.length === 0 ? (
            <span className="py-2 text-sm font-normal text-gray-700">
              <AppFormattedMessage id="NO_PLAN_FOUND" />
            </span>
          ) : (
            <InfiniteScroll
              className="flex flex-col gap-1"
              dataLength={sharePlans.length}
              hasMore={!!hasNextPage}
              loader={
                <div className="flex justify-center py-2">
                  <Loader />
                </div>
              }
              next={fetchNextPage}
              scrollableTarget={scrollableTargetId}
            >
              {sharePlans.map((plan) => {
                const optionValue =
                  field.value?.id === plan.id ? field.value : buildSharePlanFormValue(plan);
                return (
                  <FormComboboxOption
                    control={control}
                    key={plan.id}
                    name={SHARE_PLAN_FIELD}
                    value={optionValue}
                  >
                    <span className="truncate text-sm font-normal text-gray-700">{plan.name}</span>
                  </FormComboboxOption>
                );
              })}
            </InfiniteScroll>
          )}
          <Button
            className="w-full justify-start text-nowrap rounded bg-brand-25 px-2 py-[6px] text-start text-sm font-[450] text-brand-700"
            onClick={() => navigate('/pool-plans')}
            styleType="NONE"
          >
            {ADD_PLAN_BUTTON_LABEL}
          </Button>
        </div>
      </ComboboxOptions>
    </FormCombobox>
  );
};
